'use client';

import { styled } from 'styled-components';

import ArticleCard, {
  type ArticleCardProps,
} from '@dvd/blog/components/ArticleCard';
import { eyebrow } from '@dvd/blog/tokens/text-styles';

/**
 * Fragment id for the article grid. Pagination links anchor here so paging
 * lands the visitor on the list instead of the top of the page.
 */
export const ARTICLE_LIST_ID = 'articles';

export type ArticleListProps = {
  /** Optional section heading shown as an uppercase eyebrow above the grid */
  title?: string;
  /** The articles to render as cards */
  articles: ArticleCardProps[];
  /** Optional class name */
  className?: string;
};

const Wrapper = styled.section`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1.5]};
  padding-block: ${({ theme }) => theme.spacing[2]};
`;

const Eyebrow = styled.h2`
  align-self: stretch;
  color: ${({ theme }) => theme.colors.grey500};
  ${eyebrow}
  margin: 0;
  padding: 0;
`;

const CardContainer = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[1]};
  grid-template-columns: 1fr;
  width: 100%;

  ${({ theme }) => theme.mq.minWidth.medium} {
    gap: 0;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    gap: ${({ theme }) => theme.spacing[2]};
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

/**
 * Renders a heading-optional grid of blog article cards, used for archive and
 * listing pages (e.g. the “All Articles” section).
 *
 * Pass `showVideoIcon: false` on an article when it has no associated video.
 */
const ArticleList = ({ title, articles, className }: ArticleListProps) => (
  <Wrapper id={ARTICLE_LIST_ID} className={className}>
    {title ? <Eyebrow>{title}</Eyebrow> : null}
    <CardContainer>
      {articles.map((article) => (
        <ArticleCard key={article.href} {...article} />
      ))}
    </CardContainer>
  </Wrapper>
);

export default ArticleList;
